<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useKnowledgeApi } from '@/composables/api/useKnowledgeApi.ts'
import type { DocumentItem } from 'shared/src'
import { OHelperText, ONotification } from '@ai/ui-components'
import { usePasswordProtectedPdfCheck } from '@/composables/pdf/usePasswordProtectedPdfCheck'
import { useAlerts } from '@/composables/alert/useAlerts.ts'
import { alertEnum } from '@/enums/AlertEnum.ts'

const { t } = useI18n()
const { addAlert } = useAlerts()
const passwordProtectedError = ref(false)
const passwordProtectedCount = ref(1)
const { uploadDocuments, updateDocument, isUploading } = useKnowledgeApi()
const { check, isChecking: isPasswordProtectionChecking } = usePasswordProtectedPdfCheck()
const props = defineProps<{
  documentItem?: DocumentItem
}>()
const open = defineModel<boolean>('open', { default: false })
const isEditing = ref(false)
const panelOpen = ref(false)

const selectedFiles = ref<File[]>([])
const error = ref(false)
const uploaderRef = ref()
const fileInputId = 'documents-file'
const preflightController = ref<AbortController | null>(null)

const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

const isAbortError = (error: unknown) =>
  typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'

const resetUploaderInput = () => {
  const input = document.getElementById(fileInputId) as HTMLInputElement | null
  if (input) input.value = ''
}

const startPreflight = () => {
  preflightController.value?.abort()

  const controller = new AbortController()
  preflightController.value = controller

  return controller
}

const finishPreflight = (controller: AbortController) => {
  if (preflightController.value === controller) {
    preflightController.value = null
  }
}

const abortPreflight = () => {
  preflightController.value?.abort()
  preflightController.value = null
}

const rejectProtectedPdf = (count = 1) => {
  resetUploaderInput()
  passwordProtectedCount.value = count
  passwordProtectedError.value = true
}

const filterProtectedPdfFiles = async (files: File[], signal: AbortSignal) => {
  const preflightResults = await Promise.all(
    files.map(async (file) => ({
      file,
      isProtected: isPdfFile(file) ? await check(file, signal) : false,
    })),
  )

  const rejected = preflightResults.filter(({ isProtected }) => isProtected)
  return {
    acceptedFiles: preflightResults
      .filter(({ isProtected }) => !isProtected)
      .map(({ file }) => file),
    rejectedProtectedPdfCount: rejected.length,
  }
}

const openPanel = () => {
  panelOpen.value = true
  isEditing.value = !!props.documentItem
  selectedFiles.value = []
  error.value = false
}

const reset = () => {
  abortPreflight()
  selectedFiles.value = []
  isEditing.value = false
  error.value = false
  passwordProtectedError.value = false
  resetUploaderInput()
}

watch(selectedFiles, (files) => {
  if (files.length > 0) passwordProtectedError.value = false
})

const closePanel = () => {
  reset()
  panelOpen.value = false
}

watch(
  open,
  (val) => {
    if (val) {
      openPanel()
    } else closePanel()
  },
  { immediate: true },
)

watch(panelOpen, (val) => {
  if (props.documentItem) {
    isEditing.value = true
  }
  open.value = val
})

const importAndClose = async () => {
  const files = selectedFiles.value
  if (!files.length) return

  error.value = false

  try {
    let result

    if (isEditing.value && props.documentItem) {
      const file = files[0]
      const controller = startPreflight()

      try {
        if (isPdfFile(file) && (await check(file, controller.signal))) {
          rejectProtectedPdf()
          return
        }
      } catch (err) {
        if (isAbortError(err)) {
          return
        }
        throw err
      } finally {
        finishPreflight(controller)
      }

      result = await updateDocument(props.documentItem.id, file)
    } else {
      let rejectedProtectedPdfCount = 0
      let acceptedFiles: File[] = []
      const controller = startPreflight()

      try {
        ;({ acceptedFiles, rejectedProtectedPdfCount } = await filterProtectedPdfFiles(
          files,
          controller.signal,
        ))
      } catch (err) {
        if (isAbortError(err)) {
          return
        }
        throw err
      } finally {
        finishPreflight(controller)
      }

      if (rejectedProtectedPdfCount > 0) {
        rejectProtectedPdf(rejectedProtectedPdfCount)
        return
      }

      result = await uploadDocuments(acceptedFiles)
      if (result?.count && result.count > 0) {
        addAlert(t('n_documents_imported_successfully', { count: result.count }), alertEnum.Success)
      }
    }

    // Upload HTTP request succeeded - close modal and refresh list
    // (backend ingestion continues in background)
    if (result) {
      closePanel()
      emit('success')
    }
  } catch (err) {
    console.error('Upload error:', err)
    error.value = true
    // Keep modal open on error so user can retry
  }
}

const emit = defineEmits<{
  success: []
}>()

defineExpose({
  isPasswordProtectionChecking,
  openPanel,
  closePanel,
  importAndClose,
})
</script>

<template>
  <ORightPanel
    v-model:open="panelOpen"
    :title="isEditing ? t('update_documents') : t('import_documents')"
  >
    <template #header>
      <div class="flex gap-s mb-1" data-testid="documents-import-warning">
        <UiIcon name="warning" variant="solid" class="text-primary-warning" size="m" />
        <OHelperText
          data-testid="documents-import-warning"
          state="warning"
          :text="t('documents_import_warning')"
        />
      </div>
    </template>

    <div class="h-full space-y-4">
      <OFileUploader
        ref="uploaderRef"
        :id="fileInputId"
        v-model="selectedFiles"
        :accept="'.txt,.pdf,.docx,.html,.pptx,.md'"
        :max-size="1024"
        :maxSizeFile="10"
        :strict-file-limit="false"
        :error="error"
        :max-files="isEditing ? 1 : 25"
        :disabled="isUploading"
      />
      <ONotification
        v-if="passwordProtectedError"
        type="error"
        :title="
          t('knowledge.password_protected_pdf_upload_error_title', {
            count: passwordProtectedCount,
          })
        "
        :description="
          t('knowledge.password_protected_pdf_upload_error_body', { count: passwordProtectedCount })
        "
        @close="passwordProtectedError = false"
      />
    </div>

    <template #footer>
      <OButton
        :label="t('cancel')"
        variant="secondary"
        color="neutral"
        size="L"
        :disabled="isUploading"
        @mousedown.prevent
        @click="closePanel"
      />
      <OButton
        :label="t('save')"
        backgroundColor="success"
        :loading="isUploading || isPasswordProtectionChecking"
        size="L"
        @click="importAndClose"
      />
    </template>
  </ORightPanel>

  <slot :handle-click="openPanel" />
</template>
